import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayout from "../components/AppLayout";
import api from "../services/api";
import MusicTab from "./studio/MusicTab";
import SchedulerTab from "./studio/SchedulerTab";
import LiveTab from "./studio/LiveTab";
import StatsTab from "./studio/StatsTab";
import PodcastsTab from "./studio/PodcastsTab";
import HostTab from "./studio/HostTab";

const TABS = [
  { key: "music", icon: "🎵" },
  { key: "scheduler", icon: "🗓️" },
  { key: "live", icon: "🎙️" },
  { key: "host", icon: "🤖" },
  { key: "stats", icon: "📊" },
  { key: "podcasts", icon: "🎧" },
];
const TAB_KEYS = TABS.map((x) => x.key);

// Studio d'une radio : tableau de bord à onglets (Musique, Programmation,
// Direct, Statistiques, Podcasts). Onglet mémorisé dans l'URL (?tab=…).
export default function RadioStudio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [radio, setRadio] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  const tabParam = searchParams.get("tab");
  const activeTab = TAB_KEYS.includes(tabParam) ? tabParam : "music";
  const setTab = (tab) =>
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("tab", tab);
      return p;
    });

  const loadRadio = useCallback(async () => {
    try {
      const res = await api.get("/api/radios");
      const found = (res.data || []).find((r) => String(r.id) === String(id));
      setRadio(found || null);
    } catch {
      setRadio(null);
    }
  }, [id]);

  const loadPlan = useCallback(async () => {
    try {
      const r = await api.get("/api/me/plan");
      setPlan(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      await Promise.all([loadRadio(), loadPlan()]);
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, [loadRadio, loadPlan]);

  const deleteRadio = async () => {
    if (!window.confirm(t("studio.confirmDeleteRadio"))) return;
    try {
      await api.delete(`/api/radios/${id}`);
      navigate("/mes-radios");
    } catch { /* ignore */ }
  };

  if (loading) {
    return <AppLayout title={t("studio.title")}><p className="text-gray-500">{t("studio.loading")}</p></AppLayout>;
  }
  if (!radio) {
    return (
      <AppLayout title={t("studio.title")}>
        <p className="text-gray-500">
          {t("studio.notFound")} <Link to="/mes-radios" className="text-purple-600">{t("studio.backToRadios")}</Link>
        </p>
      </AppLayout>
    );
  }

  const quota = plan?.quota || {};
  const ctx = { id, radio, plan, reloadRadio: loadRadio, reloadPlan: loadPlan };

  return (
    <AppLayout
      title={radio.name}
      actions={
        <button onClick={deleteRadio} className="text-sm bg-gray-100 hover:bg-red-100 hover:text-red-600 text-gray-700 px-3 py-2 rounded-md">
          {t("studio.deleteRadio")}
        </button>
      }
    >
      {/* Barre d'onglets */}
      <div className="flex gap-2 overflow-x-auto pb-1 mb-6">
        {TABS.map(({ key, icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition ${
              activeTab === key
                ? "bg-purple-600 text-white shadow-sm"
                : "bg-white text-gray-600 hover:bg-purple-50 border border-gray-100"
            }`}
          >
            <span className="me-1.5">{icon}</span>
            {t(`studio.tabs.${key}`)}
          </button>
        ))}
      </div>

      {activeTab === "music" && <MusicTab {...ctx} />}
      {activeTab === "scheduler" && <SchedulerTab {...ctx} />}
      {activeTab === "live" && <LiveTab {...ctx} locked={!quota.live} />}
      {activeTab === "host" && <HostTab {...ctx} locked={!quota.live} />}
      {activeTab === "stats" && <StatsTab {...ctx} />}
      {activeTab === "podcasts" && <PodcastsTab {...ctx} locked={!quota.podcasts} />}
    </AppLayout>
  );
}
